import { useState } from 'preact/hooks'
import { Button } from '~/components/primitives/Button'
import { useAsyncData } from '~/hooks/useAsyncData'
import { connectionStatus, validateConnection } from '~/models/connection'
import { useDatabase } from '~/storage/DatabaseContext'
import {
  clearConnection,
  getConnection,
  saveConnection,
} from '~/storage/repositories/connectionRepository'
import { STORE } from '~/storage/schema'
import styles from './WorkerConnectionEditor.module.css'

/**
 * The access token for Project Sola's own Worker (D-095 §1).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THE FIELD IS WRITE-ONLY, AND THAT IS THE WHOLE DESIGN
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  A stored token is **never rendered back**. The screen shows that something is
 *  stored and the last four characters, which is enough to tell two tokens apart
 *  and nothing more. A credential in the DOM is a credential in a screenshot, in
 *  a screen recording, and in whatever the user is mirroring to a television —
 *  and this project's own `INSTALL_TOKEN` reached a session transcript twice by
 *  being visible where it did not need to be.
 *
 *  The input is `type="password"` with autocomplete off for the same reason:
 *  a password manager offering to save this would put the credential in a second
 *  place the user did not choose.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THE BACKUP SENTENCE IS REQUIRED COPY, NOT A COURTESY
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  D-095 §1 excludes the token from every backup and export, and requires the
 *  screen to say so. It is on screen whenever a token is stored, not tucked
 *  behind a help link, because the moment it matters is a restore — when the
 *  feature stops working for a reason the user has no way to guess.
 *
 *  **The wording says "any device", deliberately.** A restore clears every store
 *  and rewrites from a payload where `connections` is empty, so the token is
 *  gone afterwards even on the machine that made the backup. Saying "a new
 *  device" would describe only half of what happens.
 */

const PROVIDER = 'solaWorker' as const

export function WorkerConnectionEditor() {
  const db = useDatabase()
  const [draft, setDraft] = useState('')
  const [issue, setIssue] = useState<string | undefined>(undefined)
  const [busy, setBusy] = useState(false)

  const { state, reload } = useAsyncData(async () => {
    const record = await getConnection(db, PROVIDER)
    return connectionStatus(record.ok ? record.value : undefined)
  }, [STORE.connections])

  if (state.status !== 'ready') {
    return <p class={styles.body}>{state.status === 'loading' ? 'Loading…' : 'Unavailable.'}</p>
  }

  const status = state.value

  const save = async () => {
    /* Validated before the write so the message names the field, rather than
       reporting a storage failure for something that was never a storage
       problem. The same issues the model would raise, raised earlier. */
    const issues = validateConnection({ provider: PROVIDER, token: draft })
    if (issues.length > 0) {
      setIssue(issues[0]?.message)
      return
    }

    setBusy(true)
    const saved = await saveConnection(db, { provider: PROVIDER, token: draft }, new Date())
    setBusy(false)

    if (!saved.ok) {
      setIssue('That could not be saved. Try again.')
      return
    }

    /* Cleared immediately on success. The draft is the only place the plain
       token lives in memory on this screen, and leaving it in the field would
       put it back on display after the save that was supposed to hide it. */
    setDraft('')
    setIssue(undefined)
    reload()
  }

  const disconnect = async () => {
    setBusy(true)
    await clearConnection(db, PROVIDER)
    setBusy(false)
    setDraft('')
    setIssue(undefined)
    reload()
  }

  return (
    <div class={styles.editor}>
      <h3 class={styles.heading}>Second opinion access</h3>

      <p class={styles.body}>
        A second opinion is reviewed by Gemini through a service you run yourself. Paste the access
        token you set on that service. Project Sola works normally without it — this only adds the
        option to ask.
      </p>

      <p class={styles.status}>
        {status.connected ? (
          <>
            <span class={styles.connected}>Connected</span>
            <span class={styles.hint}> · ending {status.hint}</span>
          </>
        ) : (
          <span class={styles.disconnected}>Not connected</span>
        )}
      </p>

      <label class={styles.label} for="worker-token">
        {status.connected ? 'Replace the token' : 'Access token'}
      </label>
      <input
        id="worker-token"
        class={styles.input}
        type="password"
        autocomplete="off"
        spellcheck={false}
        value={draft}
        placeholder={status.connected ? 'Paste a new token' : 'Paste your token'}
        onInput={(event) => {
          setDraft((event.currentTarget as HTMLInputElement).value)
          setIssue(undefined)
        }}
      />

      {issue === undefined ? null : (
        <p class={styles.issue} role="alert">
          {issue}
        </p>
      )}

      <div class={styles.actions}>
        <Button onClick={save} disabled={busy || draft.length === 0}>
          {status.connected ? 'Replace token' : 'Save token'}
        </Button>
        {status.connected ? (
          <Button variant="secondary" onClick={disconnect} disabled={busy}>
            Disconnect
          </Button>
        ) : null}
      </div>

      {status.connected ? (
        <p class={styles.warning}>
          This token is not included in a backup or an export. Restoring a backup on any device —
          including this one — will clear it, and you will need to paste it again.
        </p>
      ) : null}
    </div>
  )
}
